import { showConfirmDialog } from '@jarvis/platform-ui';
import { listTimeOff, type TimeOffEntry } from '../../../api/timeOff';
import { formatAllDayUtcDate, formatLocalDate } from '../../../utils/timezone';
import {
  findPersistedSameDayConflicts,
  hasIntraDraftSameDayConflict,
  type TimeOffOccupancy
} from './timeOffSameDayConflict';

function padRange(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

function formatConflictLine(entry: TimeOffEntry): string {
  const name = entry.employeeDisplayName?.trim() || entry.employeeUpn;
  const when = entry.isAllDay
    ? formatAllDayUtcDate(entry.startUtc)
    : formatLocalDate(entry.startUtc);
  return `${name} (${entry.type}, ${when})`;
}

async function showConflictDialog(message: string): Promise<false> {
  await showConfirmDialog({
    title: 'Conflicting time off',
    message,
    confirmText: 'Go back',
    cancelText: 'Close'
  });
  return false;
}

/** Warn and block when drafts share a calendar day with persisted or sibling rows. Always aborts on conflict. */
export async function confirmNoSameDayTimeOffConflict(options: {
  employeeUpn: string;
  drafts: TimeOffOccupancy[];
  excludeEntryId?: string;
}): Promise<boolean> {
  const employeeUpn = options.employeeUpn.trim();
  const drafts = options.drafts;
  if (!employeeUpn || drafts.length === 0) return true;

  if (hasIntraDraftSameDayConflict(drafts)) {
    return showConflictDialog(
      'This request includes more than one time-off entry for the same employee on the same day. Change or remove the extra row first.'
    );
  }

  const starts = drafts.map((d) => d.startUtc).sort();
  const ends = drafts.map((d) => d.endUtc).sort();
  const existing = await listTimeOff({
    employeeUpn,
    from: padRange(starts[0], -1),
    to: padRange(ends[ends.length - 1], 1),
    take: 1000
  });

  const hits = findPersistedSameDayConflicts(drafts, existing, options.excludeEntryId);
  if (hits.length === 0) return true;

  const listed = hits.slice(0, 5).map(formatConflictLine).join('; ');
  const extra = hits.length > 5 ? ` and ${hits.length - 5} more` : '';
  return showConflictDialog(
    `This employee already has time off on the same day: ${listed}${extra}. Cancel or change the existing entry first.`
  );
}
